import type { Post } from "./types";
import heroManga from "@/assets/news/lan-utan-uc-manga-forfragningar.jpg";
import bildSnabblan from "@/assets/news/snabblan-mobil.jpg";

export const POSTS_13: Record<string, Post> = {
  "lan-utan-uc-manga-forfragningar": {
    title: "Lån utan UC med många förfrågningar – så gör du 2026",
    intro:
      "Söker du lån utan UC med många förfrågningar bakom dig är du i ett läge som känns tungt men som är vanligare än du tror. Varje avslag lämnar ett spår, och till slut känns det som att systemet har bestämt sig innan du ens hunnit förklara. Här går vi lugnt igenom hur förfrågningar faktiskt påverkar din kreditvärdighet, vilka långivare som prövar dig utan UC 2026, vad ett sådant lån kostar per månad – och vilka steg som gör störst skillnad för att du ska bli beviljad igen.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "28 augusti 2026",
    updated: "28 augusti 2026",
    heroSrc: heroManga,
    heroAlt: "Man vid köksbordet jämför lån utan UC med många förfrågningar på laptop och mobil",
    topLenderId: "northmill",
    bottomLenderId: "credifi",
    topLenderTitle: "Populärast vid många kreditförfrågningar",
    bottomLenderTitle: "Vår rekommendation efter flera avslag",
    reviewsLenderId: "credifi",
    cta: { to: "/lan-utan-uc", label: "Jämför långivare utan UC" },
    calculator: {
      title: "Räkna på lån utan UC – lånekalkylator",
      intro:
        "Se vad ett lån utan UC kostar innan du ansöker. Dra i reglagen för belopp och löptid så får du månadskostnad, räntekostnad, total lånekostnad och effektiv ränta.",
      note:
        "Den nominella räntan styr månadskostnaden, men det är den effektiva räntan du ska jämföra mellan långivare eftersom uppläggningsavgift och aviavgift räknas in där. Har du många förfrågningar bakom dig hamnar räntan ofta i den övre delen av spannet – då blir löptiden avgörande. Testa 12, 24 och 36 månader på samma belopp så ser du hur den totala lånekostnaden förändras i kronor.",
      amount: 20000,
      minAmount: 1000,
      maxAmount: 50000,
      months: 24,
      maxMonths: 60,
      rate: 21.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 350,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Går det att få lån utan UC med många förfrågningar?",
        a: "Ja, det är möjligt. Långivare som använder Creditsafe eller Dun & Bradstreet ser inte din förfrågningshistorik hos UC, och de har oftast en mer tillåtande kreditmodell för mindre belopp. Chansen ökar tydligt om du har fast inkomst och inga aktiva anmärkningar.",
      },
      {
        q: "Hur många kreditförfrågningar är för många?",
        a: "Det finns ingen officiell gräns. Erfarenhetsmässigt börjar storbankerna reagera vid fyra till sex förfrågningar under tolv månader, medan nischbanker ofta accepterar fler. Det är kombinationen av många förfrågningar och låg marginal i hushållsbudgeten som ger avslag, inte antalet i sig.",
      },
      {
        q: "Hur länge ligger en kreditförfrågan kvar?",
        a: "En förfrågan hos UC ligger kvar i tolv månader och syns för alla som därefter tar en upplysning på dig. Hos Creditsafe är lagringstiden också tolv månader. Efter det försvinner posten automatiskt utan att du behöver göra något.",
      },
      {
        q: "Påverkar en förfrågan min kreditvärdighet direkt?",
        a: "Själva förfrågan sänker inte din poäng lika mycket som många tror. Det är mönstret som räknas: flera förfrågningar tätt inpå varandra tolkas som att du söker pengar på flera håll samtidigt, vilket bankerna läser som ansträngd likviditet.",
      },
      {
        q: "Är det bättre att söka via en låneförmedlare?",
        a: "Ofta ja. En förmedlare tar en enda kreditupplysning och skickar din ansökan till upp till 40 banker. Det ger många bud till priset av en förfrågan, i stället för fem separata upplysningar när du söker själv.",
      },
      {
        q: "Syns lånet någon annanstans om långivaren inte tar UC?",
        a: "Ja. Krediten rapporteras till det upplysningsbolag långivaren använder och sedan 2023 rapporteras alla konsumentkrediter dessutom till Skuldregistret. Ett lån utan UC är osynligt i UC:s förfrågningshistorik, inte i systemet som helhet.",
      },
      {
        q: "Hur snabbt kan jag få pengarna?",
        a: "Signerar du med BankID och har konto i en bank som stödjer realtidsbetalningar kan pengarna vara framme inom några minuter. Vid manuell handläggning eller inkomstkontroll sker utbetalningen normalt nästa bankdag.",
      },
      {
        q: "Vad gör jag om jag får avslag ändå?",
        a: "Vänta minst tre månader, be om avbetalningsplan hos den fordran som pressar mest, och begär ut din kreditupplysning gratis för att kontrollera att uppgifterna stämmer. En medsökande är den enskilt starkaste faktorn för att vända ett nej till ett ja.",
      },
    ],
    blocks: [
      { t: "p", text: "Det första du ska veta är att många förfrågningar inte gör dig omöjlig att låna till. Det gör dig svårbedömd för just de banker som har hårdast automatiska filter – och det finns långivare som räknar annorlunda." },
      { t: "p", text: "Den här guiden är skriven utan pekpinnar. Du får siffrorna, kraven och alternativen, och sedan bestämmer du själv vad som är rätt för din situation." },

      { t: "h2", text: "Lån utan UC med många förfrågningar – kort svar först" },
      { t: "p", text: "Ja, det går. Ett lån utan UC innebär att kreditprövningen görs hos Creditsafe eller Dun & Bradstreet i stället för hos UC. Dina UC-förfrågningar syns då inte i underlaget, och bedömningen görs i stället på inkomst, betalningshistorik och eventuella anmärkningar." },
      {
        t: "stats",
        items: [
          { label: "Vanligt beloppsspann", value: "1 000–50 000 kr" },
          { label: "Typisk ränta 2026", value: "12–29 %" },
          { label: "Lagringstid förfrågan", value: "12 månader" },
        ],
      },
      { t: "h3", text: "Vad en kreditförfrågan egentligen är" },
      { t: "p", text: "En kreditförfrågan är en notering om att någon har hämtat information om din ekonomi i samband med en kreditansökan. Noteringen innehåller datum och vem som frågade – inte om du blev beviljad eller nekad. Det är därför avslag inte syns, men mönstret av ansökningar gör det." },
      { t: "h3", text: "Därför reagerar bankerna på antalet" },
      { t: "p", text: "En bank som ser sex förfrågningar på tre månader vet inte om du jämför alternativ eller om du desperat söker pengar. Den automatiska modellen väljer det försiktiga svaret. Det är sällan personligt, och det säger ingenting om din vilja att betala." },

      { t: "h2", text: "Så påverkar antalet förfrågningar din chans" },
      { t: "p", text: "Nedan är en förenklad bild av hur svenska långivare brukar reagera. Siffrorna är erfarenhetsbaserade riktmärken, inte officiella regler." },
      {
        t: "table",
        caption: "Ungefärlig bedömning utifrån antal kreditförfrågningar senaste 12 månaderna",
        head: ["Antal förfrågningar", "Storbank", "Nischbank", "Långivare utan UC"],
        rows: [
          ["0–2", "God chans", "God chans", "God chans"],
          ["3–5", "Osäkert", "God chans", "God chans"],
          ["6–9", "Oftast avslag", "Osäkert", "Rimlig chans"],
          ["10+", "Avslag", "Oftast avslag", "Osäkert"],
        ],
      },
      { t: "p", text: "Poängen med tabellen är enkel: ju fler förfrågningar du har, desto viktigare blir det att söka rätt från början i stället för att söka brett." },

      { t: "h2", text: "Vad kostar ett lån utan UC 2026?" },
      { t: "p", text: "Räkneexemplen utgår från 21,9 procent nominell ränta, 350 kr uppläggningsavgift och 29 kr aviavgift – en realistisk nivå när du har flera förfrågningar bakom dig." },
      {
        t: "table",
        caption: "Räkneexempel: 20 000 kr, 21,9 % nominell ränta, 350 kr uppläggning, 29 kr avi",
        head: ["Löptid", "Månadskostnad", "Total lånekostnad", "Effektiv ränta"],
        rows: [
          ["12 mån", "1 903 kr", "2 836 kr", "28,6 %"],
          ["24 mån", "1 065 kr", "5 560 kr", "26,3 %"],
          ["36 mån", "793 kr", "8 348 kr", "25,4 %"],
          ["48 mån", "660 kr", "11 030 kr", "24,9 %"],
          ["60 mån", "583 kr", "14 330 kr", "24,6 %"],
        ],
      },
      { t: "p", text: "Lägg märke till att den effektiva räntan sjunker något med längre löptid samtidigt som totalkostnaden femdubblas. Löptiden, inte räntan, är den variabel som påverkar plånboken mest." },
      { t: "h3", text: "Räntetak och kostnadstak skyddar dig" },
      { t: "p", text: "Enligt konsumentkreditlagen får den nominella räntan aldrig överstiga Riksbankens referensränta plus 20 procentenheter, och de samlade kostnaderna får aldrig överstiga lånebeloppet. Lånar du 20 000 kr kan kostnaderna alltså aldrig bli mer än 20 000 kr. Reglerna övervakas av [Finansinspektionen](https://www.fi.se) och [Konsumentverket](https://www.konsumentverket.se)." },
      {
        t: "box",
        tone: "tip",
        title: "Tre saker som sänker din kostnad direkt",
        items: [
          "Välj kortast löptid som din månadsbudget klarar med marginal.",
          "Be om e-faktura eller autogiro – många långivare tar då bort aviavgiften.",
          "Amortera extra när du kan; räntan beräknas alltid på kvarvarande skuld.",
        ],
      },

      { t: "h2", text: "Vilka långivare prövar dig utan UC?" },
      { t: "p", text: "Marknaden består av direktlångivare som prövar dig själva och förmedlare som skickar ansökan vidare. Har du många förfrågningar är direktlångivarna utan UC oftast den snabbaste vägen till ett besked." },
      { t: "h3", text: "Direktlångivare utan UC" },
      { t: "p", text: "Hit hör bland andra Northmill, Credifi, Lumify, Ferratum och Saldo. De använder Creditsafe eller Dun & Bradstreet, har helt digital ansökan och betalar ofta ut samma dag. Se aktuella villkor på vår sida om [lån utan UC](/lan-utan-uc)." },
      { t: "h3", text: "Förmedlare med en enda kreditupplysning" },
      { t: "p", text: "Lendo, Sambla, Enklare och Advisa tar UC, men bara en gång, och skickar sedan ansökan till upp till 40 banker. Har du inga anmärkningar är det nästan alltid den billigaste vägen – och det är faktiskt sättet att minska antalet framtida förfrågningar." },
      { t: "img", src: bildSnabblan, alt: "Person ansöker om lån utan UC i mobilen efter flera kreditförfrågningar" },

      {
        t: "proscons",
        title: "Lån utan UC vid många förfrågningar",
        pros: [
          "Din UC-historik påverkar inte bedömningen",
          "Snabbt besked, ofta inom minuter",
          "Ingen ny förfrågan hos UC registreras",
          "Fungerar även med tunn kredithistorik",
        ],
        cons: [
          "Högre ränta än ett vanligt privatlån",
          "Lägre maxbelopp, oftast 50 000 kr",
          "Krediten rapporteras ändå till Skuldregistret",
          "Löser inte en underliggande obalans i budgeten",
        ],
      },

      { t: "h2", text: "Krav som gäller 2026" },
      {
        t: "ul",
        items: [
          "Minst 18 år, flera långivare kräver 20 eller 21 år.",
          "Folkbokförd i Sverige med svenskt personnummer och BankID.",
          "Regelbunden inkomst som går att styrka – lön, pension, a-kassa eller sjukersättning godtas hos flera.",
          "Svenskt bankkonto i eget namn.",
          "Normalt ingen pågående skuld hos [Kronofogden](https://www.kronofogden.se).",
        ],
      },

      { t: "h2", text: "Så minskar du antalet förfrågningar framöver" },
      {
        t: "steps",
        title: "Fem steg som faktiskt gör skillnad",
        items: [
          "Sluta söka brett. Varje ny ansökan försämrar chansen för nästa.",
          "Begär ut din kreditupplysning gratis och kontrollera att uppgifterna stämmer.",
          "Använd en förmedlare i stället för att söka hos fem banker separat.",
          "Vänta tre månader om du nyligen fått flera avslag – tiden arbetar för dig.",
          "Lägg till en medsökande om det är möjligt; det höjer beviljandegraden mest av allt.",
        ],
      },
      { t: "h3", text: "Varför väntetiden hjälper" },
      { t: "p", text: "Eftersom förfrågningar rullar av efter tolv månader förbättras din profil automatiskt varje månad du avstår från att ansöka. Tre lugna månader kan vara skillnaden mellan ett nej och ett ja, helt utan att din inkomst har förändrats." },

      { t: "h2", text: "Alternativ som ofta är billigare" },
      {
        t: "ul",
        items: [
          "Avbetalningsplan direkt hos fordringsägaren – nästan alltid räntefritt eller billigare.",
          "Utnyttja ett befintligt kort eller en befintlig kredit med lägre ränta.",
          "Samla dyra krediter i ett [samlingslån](/samla-lan) och sänk månadskostnaden.",
          "Kontakta budget- och skuldrådgivningen i din kommun – tjänsten är gratis.",
        ],
      },
      { t: "p", text: "Räkna alltid på alternativen i vår [lånekalkyl](/guider/lanekalkyl) innan du signerar. Att se totalkostnaden i kronor gör beslutet enklare än att jämföra procent." },

      { t: "h2", text: "Vanliga misstag att undvika" },
      {
        t: "box",
        tone: "warning",
        title: "Fyra fällor vid många förfrågningar",
        items: [
          "Att skicka in fem ansökningar samma dag i hopp om att någon säger ja.",
          "Att välja längsta löptiden av vana och betala dubbelt i ränta.",
          "Att jämföra nominell ränta i stället för effektiv ränta.",
          "Att ta ett nytt lån för att betala ett gammalt utan att ändra budgeten.",
        ],
      },

      { t: "h2", text: "Din ångerrätt och dina rättigheter" },
      { t: "p", text: "Du har alltid 14 dagars ångerrätt enligt konsumentkreditlagen. Du betalar då tillbaka lånebeloppet plus ränta för de dagar du haft pengarna, medan uppläggningsavgiften får behållas av långivaren. Du har också alltid rätt att lösa lånet i förtid utan extra kostnad." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Många förfrågningar är ett hinder, inte ett stopp. Ett lån utan UC kan ge dig ett ja här och nu, men det är dyrare än ett vanligt privatlån. Sök riktat i stället för brett, jämför effektiv ränta, välj kortast möjliga löptid – och låt de gamla förfrågningarna rulla av innan du tar nästa steg." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Finansinspektionen](https://www.fi.se) – tillstånd och tillsyn av kreditgivare",
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Kronofogden](https://www.kronofogden.se) – betalningsanmärkningar och skuldsanering",
        ],
      },
    ],
  },
};
